import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const hasSupabaseConfig = Boolean(
  supabaseUrl &&
  supabaseAnonKey &&
  !supabaseUrl.includes('placeholder.supabase.co')
);

if (!hasSupabaseConfig) {
  console.warn(
    'Supabase credentials not found. Create a .env file with REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY, then restart npm start.'
  );
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key'
);

// Helper function to upload image to Supabase Storage
export const uploadImage = async (file, sessionName, designerName) => {
  console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type);
  
  const fileExt = file.name.split('.').pop();
  const fileName = `${sessionName}/${Date.now()}_${designerName.replace(/\s+/g, '_')}.${fileExt}`;
  
  console.log('Uploading to path:', fileName);
  console.log('Storage bucket: design-submissions');
  
  const { data, error } = await supabase.storage
    .from('design-submissions')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false
    });

  if (error) {
    console.error('Supabase storage upload error:', error);
    console.error('Error code:', error.statusCode);
    console.error('Error message:', error.message);
    throw new Error(`Upload failed: ${error.message}`);
  }

  console.log('Upload successful, data:', data);

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('design-submissions')
    .getPublicUrl(fileName);

  console.log('Public URL generated:', publicUrl);

  return { path: data.path || fileName, url: publicUrl };
};

// Helper function to get image preview (base64 for immediate display)
export const getImagePreview = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Helper function to upload designer avatar to Supabase Storage
export const uploadDesignerAvatar = async (file, designerName, gameId) => {
  console.log('Uploading designer avatar:', file.name, 'for designer:', designerName);
  
  const fileExt = file.name.split('.').pop();
  const fileName = `designer-avatars/${gameId}/${Date.now()}_${designerName.replace(/\s+/g, '_')}.${fileExt}`;
  
  console.log('Uploading to path:', fileName);
  console.log('Storage bucket: design-submissions');
  
  const { data, error } = await supabase.storage
    .from('design-submissions')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: true // Allow overwriting existing avatars
    });

  if (error) {
    console.error('Supabase storage upload error:', error);
    throw new Error(`Upload failed: ${error.message}`);
  }

  console.log('Upload successful, data:', data);

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('design-submissions')
    .getPublicUrl(fileName);

  console.log('Public URL generated:', publicUrl);

  return { path: data.path || fileName, url: publicUrl };
};

// Helper function to upload user profile avatar to Supabase Storage
export const uploadUserAvatar = async (file, userId) => {
  console.log('Uploading user avatar:', file.name, 'for user:', userId);
  
  // Validate file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid file type. Please upload an image (JPEG, PNG, GIF, or WebP).');
  }
  
  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    throw new Error('File size too large. Please upload an image smaller than 5MB.');
  }
  
  const fileExt = file.name.split('.').pop();
  const fileName = `user-avatars/${userId}/${Date.now()}.${fileExt}`;
  
  console.log('Uploading to path:', fileName);
  console.log('Storage bucket: design-submissions');
  
  const { data, error } = await supabase.storage
    .from('design-submissions')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: true // Allow overwriting existing avatars
    });

  if (error) {
    console.error('Supabase storage upload error:', error);
    throw new Error(`Upload failed: ${error.message}`);
  }

  console.log('Upload successful, data:', data);

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('design-submissions')
    .getPublicUrl(fileName);

  console.log('Public URL generated:', publicUrl);

  return { path: data.path || fileName, url: publicUrl };
};

